// Alertes (F2 ; charte D-038) : liste filtrable triée par gravité, compteurs par niveau, notifications internes ;
// fiche d'une alerte (mesure, règle, échéance, escalades, historique) avec ses actions (acquitter, affecter un
// responsable, consigner une action, résoudre avec motif, rouvrir) ; règles d'alerte paramétrables avec
// historique pour l'administration. Les droits viennent du serveur.

import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CODES_PROVINCES, formaterNombre } from '@carburanttrack/shared';
import { api, messageErreur, type Reponse } from '../../api/client';
import { useFiltres } from '../../app/filtres';
import { Message, MessageErreur, Squelette } from '../../composants/Etats';
import { dateHeure, jourLong, libelleProvince } from '../../composants/format';
import { EtiquetteNiveau, LIBELLES_NIVEAUX, quantite, tonDeNiveau } from './composants';
import type { Alerte, FicheAlerte, Notification, RegleAlerte } from './types';

const DECLENCHEURS: Record<string, string> = {
  autonomie_station: 'Autonomie station',
  autonomie_depot: 'Autonomie dépôt',
  autonomie_province: 'Autonomie province',
  livraison_en_retard: 'Livraison en retard',
  cargaison_reportee: 'Cargaison reportée',
  declaration_manquante: 'Déclaration manquante',
  ecart_stock: 'Écart de stock',
  concentration_ruptures: 'Concentration de ruptures',
  penurie_gpl: 'Pénurie de GPL',
};

const LIBELLES_MESURES: Record<string, string> = {
  jours: 'Autonomie (jours)',
  confiance: 'Confiance',
  motif: 'Motif du calcul',
  stockUtilisable: 'Stock utilisable',
  consommationMoyenne: 'Consommation moyenne par jour',
  joursReport: 'Report (jours)',
  autonomieZone: 'Autonomie de la zone (jours)',
  echeancesManquees: 'Échéances manquées',
  ruptures: 'Stations en rupture',
  stations: 'Stations de la province',
  partPourcentage: 'Part en rupture (%)',
  maxMemeVille: 'Au plus dans une même ville',
  rapportTolerance: 'Rapport à la tolérance',
  tolerancePourcentage: 'Tolérance (%)',
  ecart: 'Écart',
  ecartRelatifPct: 'Écart relatif (%)',
};

/** Mesures exprimées dans l'unité du produit (champ `unite` de la mesure). */
const MESURES_QUANTITES = new Set(['stockUtilisable', 'consommationMoyenne', 'ecart']);

function valeurMesure(v: unknown): string {
  if (v === null || v === undefined) return 'indisponible';
  if (typeof v === 'number') return formaterNombre(v, Number.isInteger(v) ? 0 : 1);
  if (typeof v === 'string' && /^-?\d+(\.\d+)?$/.test(v)) return formaterNombre(v, v.includes('.') ? 1 : 0);
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) return dateHeure(v);
  if (typeof v === 'string') return v;
  return '';
}

export function PageAlertes() {
  const [params, setParams] = useSearchParams();
  const { province: provinceGlobale } = useFiltres();
  const etat = params.get('etat') ?? 'ouvertes';
  const niveau = params.get('niveau') ?? '';
  const declencheur = params.get('declencheur') ?? '';
  const province = params.get('province') ?? provinceGlobale ?? '';
  const filtres = new URLSearchParams({ etat });
  if (niveau) filtres.set('niveau', niveau);
  if (declencheur) filtres.set('declencheur', declencheur);
  if (province) filtres.set('province', province);
  const liste = useQuery({ queryKey: ['alertes', 'liste', filtres.toString()], queryFn: async () => api<Alerte[]>(`/api/v1/alertes?${filtres.toString()}`) });
  const regles = useQuery({ queryKey: ['alertes', 'regles'], queryFn: async () => api<RegleAlerte[]>('/api/v1/regles-alerte'), retry: false });
  const changer = (cle: string, valeur: string) => {
    const p = new URLSearchParams(params);
    if (valeur) p.set(cle, valeur);
    else p.delete(cle);
    setParams(p, { replace: true });
  };
  const compteurs = (liste.data?.meta.ouvertesParNiveau ?? {}) as Record<string, number>;
  return (
    <div className="page-alertes">
      <div className="page-tete">
        <div>
          <span className="contexte">{jourLong()}</span>
          <h1>Alertes</h1>
          <p>Classées par gravité. Une alerte non acquittée dans son délai de traitement passe au niveau hiérarchique suivant.</p>
        </div>
        {regles.data && (
          <Link className="bouton" to="/alertes/regles">
            Règles d’alerte
          </Link>
        )}
      </div>
      <div className="compteurs joints" role="list" aria-label="Alertes ouvertes par niveau">
        {(['critique', 'elevee', 'preventive', 'informative'] as const).map((n) => (
          <button key={n} type="button" role="listitem" className="compteur-bouton" aria-pressed={niveau === n} onClick={() => changer('niveau', niveau === n ? '' : n)}>
            {LIBELLES_NIVEAUX[n]}
            <b className={compteurs[n] && (n === 'critique' || n === 'elevee') ? 'rouge' : compteurs[n] ? 'ambre' : undefined}>{formaterNombre(compteurs[n] ?? 0)}</b>
          </button>
        ))}
      </div>
      <div className="barre-filtres" role="search" aria-label="Filtres des alertes">
        <div className="champ">
          <label htmlFor="al-etat">État</label>
          <select id="al-etat" value={etat} onChange={(e) => changer('etat', e.target.value)}>
            <option value="ouvertes">Ouvertes</option>
            <option value="resolues">Résolues ou remplacées</option>
            <option value="toutes">Toutes</option>
          </select>
        </div>
        <div className="champ">
          <label htmlFor="al-declencheur">Déclencheur</label>
          <select id="al-declencheur" value={declencheur} onChange={(e) => changer('declencheur', e.target.value)}>
            <option value="">Tous</option>
            {Object.entries(DECLENCHEURS).map(([c, l]) => (
              <option key={c} value={c}>
                {l}
              </option>
            ))}
          </select>
        </div>
        <div className="champ">
          <label htmlFor="al-province">Province</label>
          <select id="al-province" value={province} onChange={(e) => changer('province', e.target.value)}>
            <option value="">Toutes</option>
            {CODES_PROVINCES.map((c) => (
              <option key={c} value={c}>
                {libelleProvince(c)}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="deux-colonnes">
        <section className="principal" aria-label="Liste des alertes">
          {liste.isLoading && <Squelette lignes={6} />}
          {liste.error && <MessageErreur erreur={liste.error} titre="Alertes indisponibles" />}
          {liste.data && liste.data.donnees.length === 0 && (
            <div className="etat-vide">
              <h2>Aucune alerte pour ces filtres</h2>
              <p>Le moteur d’alertes évalue les déclencheurs à chaque événement et toutes les 15 minutes.</p>
            </div>
          )}
          {liste.data && liste.data.donnees.length > 0 && (
            <ul className="liste-alertes-compacte">
              {liste.data.donnees.map((a) => (
                <li key={a.id}>
                  <Link className={`alerte ${a.etat === 'resolue' || a.etat === 'remplacee' ? '' : a.niveau}`} to={`/alertes/${a.id}`}>
                    <i aria-hidden="true" />
                    <span>
                      <span className="meta">
                        <EtiquetteNiveau niveau={a.niveau} />
                        <span>{a.declencheurLibelle}</span>
                        <span>{a.etatLibelle}</span>
                      </span>
                      <b>{a.titre}</b>
                      <small className="attenue">
                        {a.description}
                        {a.responsable ? `, responsable ${a.responsable}` : ''}
                        {`, dernière détection ${dateHeure(a.derniereDetection)}`}
                      </small>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
        <aside className="lateral">
          <Notifications />
        </aside>
      </div>
    </div>
  );
}

function Notifications() {
  const client = useQueryClient();
  // Centre de notifications du module H2 ; la clé « notifications » est actualisée par le flux temps réel.
  const liste = useQuery({ queryKey: ['notifications', 'alertes'], queryFn: async () => api<Notification[]>('/api/v1/notifications?limite=12') });
  const toutLire = useMutation({ mutationFn: async () => api('/api/v1/notifications/lecture-globale', { corps: {} }), onSuccess: () => client.invalidateQueries({ queryKey: ['notifications'] }) });
  const lire = useMutation({ mutationFn: async (id: string) => api(`/api/v1/notifications/${id}/lecture`, { corps: {} }), onSuccess: () => client.invalidateQueries({ queryKey: ['notifications'] }) });
  const nonLues = (liste.data?.meta.nonLues as number | undefined) ?? 0;
  return (
    <section className="carte-bloc" aria-labelledby="titre-notifications">
      <div className="carte-bloc-tete">
        <h2 id="titre-notifications">Mes notifications</h2>
        <span className="attenue">{formaterNombre(nonLues)} non lue{nonLues > 1 ? 's' : ''}</span>
      </div>
      <p className="attenue">
        Canaux et préférences par type dans le <Link to="/notifications">centre de notifications</Link>.
      </p>
      {liste.data && liste.data.donnees.length === 0 && <p className="attenue">Aucune notification.</p>}
      {liste.data && liste.data.donnees.length > 0 && (
        <ul className="liste-notifications">
          {liste.data.donnees.slice(0, 12).map((n) => (
            <li key={n.id} className={n.luLe ? 'lue' : undefined}>
              <b>{n.titre}</b>
              <small>
                {dateHeure(n.creeLe)}
                {n.luLe ? ', lue' : ''}
              </small>
              <span className="actions">
                {n.lien && <Link to={n.lien}>Ouvrir</Link>}
                {!n.luLe && (
                  <button type="button" className="bouton discret" onClick={() => lire.mutate(n.id)}>
                    Marquer comme lue
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      {nonLues > 0 && (
        <button type="button" className="bouton" onClick={() => toutLire.mutate()} disabled={toutLire.isPending}>
          Tout marquer comme lu
        </button>
      )}
    </section>
  );
}

export function PageFicheAlerte() {
  const { id } = useParams();
  const fiche = useQuery({ queryKey: ['alertes', 'fiche', id], queryFn: async () => (await api<FicheAlerte>(`/api/v1/alertes/${id}`)).donnees });
  if (fiche.isLoading) return <Squelette lignes={8} libelle="Chargement de l’alerte" />;
  if (fiche.error) return <MessageErreur erreur={fiche.error} titre="Alerte indisponible" />;
  const f = fiche.data!;
  const a = f.alerte;
  const ouverte = ['ouverte', 'acquittee', 'en_traitement'].includes(a.etat);
  const ficheStock = Boolean(fiche.data?.droits.ficheStock);
  const lienObjet = a.objetType === 'cargaison' ? (f.droits.ficheCargaison ? `/approvisionnements/cargaisons/${a.objetId}` : null) : a.installationId ? (ficheStock ? `/stocks/installations/${a.installationId}` : `/referentiel/installations/${a.installationId}`) : null;
  const libelleObjet = a.objetType === 'cargaison' ? 'Voir la cargaison' : !ficheStock ? 'Fiche de l’installation' : a.installationNom ? `Voir le stock : ${a.installationNom}` : 'Voir le stock de l’installation';
  return (
    <div className="page-fiche-alerte">
      <div className="page-tete">
        <div>
          <span className="contexte">
            {a.declencheurLibelle}
            {a.provinceCode ? `, ${libelleProvince(a.provinceCode)}` : ''}
            {a.organisationNom ? `, ${a.organisationNom}` : ''}
          </span>
          <h1>{a.titre}</h1>
          <p>
            <EtiquetteNiveau niveau={a.niveau} /> <span className="etiquette">{a.etatLibelle}</span>
            {a.niveauEscalade > 0 && <span className="etiquette ambre">Escaladée au {a.niveauEscalade === 1 ? 'superviseur de zone' : 'niveau DGH'}</span>}
          </p>
        </div>
        {lienObjet && (
          <Link className="bouton" to={lienObjet}>
            {libelleObjet}
          </Link>
        )}
      </div>
      <section className={`a-faire${tonDeNiveau(a.niveau) === 'rouge' ? ' rouge' : ''}${ouverte ? '' : ' neutre'}`} aria-label="Constat">
        <div>
          <span className="sur-titre">{ouverte ? 'Constat' : 'Alerte close'}</span>
          <h2>{a.description}</h2>
          <p>
            Détectée le {dateHeure(a.premiereDetection)}, dernière détection le {dateHeure(a.derniereDetection)} ({a.nombreDetections} détection{a.nombreDetections > 1 ? 's' : ''}).{' '}
            {ouverte ? `À traiter avant le ${dateHeure(a.dateLimite)}.` : a.motifResolution ? `Motif : ${a.motifResolution}` : ''}
          </p>
        </div>
      </section>
      <div className="deux-colonnes">
        <div className="principal">
          {f.droits.agir && <ActionsAlerte fiche={f} />}
          <section className="carte-bloc" aria-labelledby="titre-historique-alerte">
            <h2 id="titre-historique-alerte">Historique</h2>
            <ol className="chronologie">
              {f.historique.map((h) => (
                <li key={h.id}>
                  {LIBELLES_ACTIONS[h.type] ?? h.type}
                  {h.commentaire ? ` : ${h.commentaire}` : ''}
                  <small>
                    {dateHeure(h.creeLe)}, {h.auteurNom ? `${h.auteurPrenom} ${h.auteurNom}` : 'moteur d’alertes'}
                  </small>
                </li>
              ))}
            </ol>
          </section>
        </div>
        <aside className="lateral">
          <section className="carte-bloc" aria-labelledby="titre-mesure">
            <h2 id="titre-mesure">Mesure</h2>
            {a.mesure ? (
              <dl className="paires mesures-alerte">
                {Object.entries(a.mesure)
                  .filter(([k, v]) => LIBELLES_MESURES[k] && v !== null && typeof v !== 'object')
                  .map(([k, v]) => (
                    <div key={k}>
                      <dt>{LIBELLES_MESURES[k]}</dt>
                      <dd>{MESURES_QUANTITES.has(k) && typeof a.mesure!.unite === 'string' ? quantite(String(v), a.mesure!.unite) : valeurMesure(v)}</dd>
                    </div>
                  ))}
              </dl>
            ) : (
              <p className="attenue">Aucune mesure associée.</p>
            )}
          </section>
          <section className="carte-bloc" aria-labelledby="titre-traitement">
            <h2 id="titre-traitement">Traitement</h2>
            <dl className="paires">
              <div>
                <dt>Responsable</dt>
                <dd>{f.responsable ? `${f.responsable.prenom} ${f.responsable.nom}` : 'non désigné'}</dd>
              </div>
              <div>
                <dt>Règle</dt>
                <dd>{f.regle ? `${f.regle.libelle}, version ${f.regle.version}` : 'indisponible'}</dd>
              </div>
              <div>
                <dt>Délai de traitement</dt>
                <dd>{f.regle ? `${f.regle.delaisHeures[a.niveau] ?? 'indisponible'} h` : 'indisponible'}</dd>
              </div>
            </dl>
            {f.escalades.length > 0 && (
              <ol className="chronologie espace-haut">
                {f.escalades.map((e) => (
                  <li key={e.id}>
                    Escalade au {e.versNiveau === 1 ? 'superviseur de zone' : 'niveau DGH'}
                    <small>
                      {dateHeure(e.creeLe)}, {e.motif}, {e.destinataires} destinataire{e.destinataires > 1 ? 's' : ''}
                    </small>
                  </li>
                ))}
              </ol>
            )}
            {(a.remplaceId || a.remplaceeParId) && (
              <p className="espace-haut">
                {a.remplaceId && <Link to={`/alertes/${a.remplaceId}`}>Alerte de niveau précédent</Link>}
                {a.remplaceeParId && <Link to={`/alertes/${a.remplaceeParId}`}>Alerte de niveau suivant</Link>}
              </p>
            )}
          </section>
        </aside>
      </div>
    </div>
  );
}

const LIBELLES_ACTIONS: Record<string, string> = {
  creation: 'Création',
  changement_niveau: 'Changement de niveau',
  acquittement: 'Acquittement',
  affectation: 'Affectation d’un responsable',
  action: 'Action consignée',
  resolution: 'Résolution',
  resolution_automatique: 'Résolution automatique',
  reouverture: 'Réouverture',
  escalade: 'Escalade',
};

function ActionsAlerte({ fiche }: { fiche: FicheAlerte }) {
  const client = useQueryClient();
  const a = fiche.alerte;
  const ouverte = ['ouverte', 'acquittee', 'en_traitement'].includes(a.etat);
  const [commentaire, setCommentaire] = useState('');
  const [responsableId, setResponsableId] = useState('');
  const responsables = useQuery({
    queryKey: ['alertes', 'responsables', a.id],
    queryFn: async () => (await api<{ id: string; prenom: string; nom: string; fonction: string | null; organisationNom: string | null }[]>(`/api/v1/alertes/${a.id}/responsables`)).donnees,
    enabled: ouverte,
  });
  const action = useMutation({
    mutationFn: async (nom: string) => api<Reponse<unknown>>(`/api/v1/alertes/${a.id}/actions`, { corps: { action: nom, commentaire: commentaire.trim() || null, responsableId: nom === 'affecter' ? responsableId : null } }),
    onSuccess: () => {
      setCommentaire('');
      void client.invalidateQueries({ queryKey: ['alertes'] });
    },
  });
  return (
    <section className="panneau-saisie" aria-labelledby="titre-actions-alerte">
      <h2 id="titre-actions-alerte">Agir sur l’alerte</h2>
      <div className="champ">
        <label htmlFor="al-commentaire">{ouverte ? 'Action, commentaire ou motif de résolution' : 'Motif de réouverture'}</label>
        <textarea id="al-commentaire" value={commentaire} onChange={(e) => setCommentaire(e.target.value)} maxLength={2000} />
      </div>
      {ouverte && (
        <div className="champ">
          <label htmlFor="al-responsable">Responsable du traitement</label>
          <select id="al-responsable" value={responsableId} onChange={(e) => setResponsableId(e.target.value)}>
            <option value="">Choisir</option>
            {(responsables.data ?? []).map((r) => (
              <option key={r.id} value={r.id}>
                {r.prenom} {r.nom}
                {r.fonction ? `, ${r.fonction}` : ''}
              </option>
            ))}
          </select>
        </div>
      )}
      {action.error && <Message type="erreur">{messageErreur(action.error)}</Message>}
      {action.isSuccess && <Message type="succes">Action enregistrée et tracée.</Message>}
      <div className="actions">
        {a.etat === 'ouverte' && (
          <button type="button" className="bouton primaire" disabled={action.isPending} onClick={() => action.mutate('acquitter')}>
            Acquitter
          </button>
        )}
        {ouverte && (
          <button type="button" className="bouton" disabled={action.isPending || !responsableId} onClick={() => action.mutate('affecter')}>
            Affecter
          </button>
        )}
        {ouverte && (
          <button type="button" className="bouton" disabled={action.isPending || !commentaire.trim()} onClick={() => action.mutate('consigner')}>
            Consigner l’action
          </button>
        )}
        {ouverte && (
          <button type="button" className="bouton" disabled={action.isPending || !commentaire.trim()} onClick={() => action.mutate('resoudre')}>
            Résoudre avec ce motif
          </button>
        )}
        {a.etat === 'resolue' && (
          <button type="button" className="bouton" disabled={action.isPending || !commentaire.trim()} onClick={() => action.mutate('rouvrir')}>
            Rouvrir
          </button>
        )}
      </div>
    </section>
  );
}

export function PageReglesAlerte() {
  const client = useQueryClient();
  const regles = useQuery({ queryKey: ['alertes', 'regles'], queryFn: async () => api<RegleAlerte[]>('/api/v1/regles-alerte') });
  const [choisie, setChoisie] = useState<string | null>(null);
  if (regles.isLoading) return <Squelette lignes={6} />;
  if (regles.error) return <MessageErreur erreur={regles.error} titre="Règles indisponibles" />;
  const modifiable = Boolean(regles.data!.meta.modifiable);
  const selection = regles.data!.donnees.find((r) => r.id === choisie) ?? null;
  return (
    <div className="page-regles-alerte">
      <div className="page-tete">
        <div>
          <h1>Règles d’alerte</h1>
          <p>Seuils par niveau, délais de traitement et destinataires ; chaque modification est historisée avec son motif et son auteur.</p>
        </div>
      </div>
      {selection && <EditionRegle regle={selection} modifiable={modifiable} fermer={() => setChoisie(null)} rafraichir={() => client.invalidateQueries({ queryKey: ['alertes', 'regles'] })} />}
      <div className="tableau-conteneur">
        <table className="tableau">
          <caption className="visuellement-cache">Règles d’alerte</caption>
          <thead>
            <tr>
              <th scope="col">Règle</th>
              {(['informative', 'preventive', 'elevee', 'critique'] as const).map((n) => (
                <th key={n} scope="col" className="nombre">
                  {LIBELLES_NIVEAUX[n]}
                </th>
              ))}
              <th scope="col">État</th>
            </tr>
          </thead>
          <tbody>
            {regles.data!.donnees.map((r) => (
              <tr key={r.id} aria-selected={r.id === choisie}>
                <td>
                  <button type="button" className="bouton-ligne" onClick={() => setChoisie(r.id)}>
                    {r.libelle}
                    <small>
                      {r.declencheurLibelle}, version {r.version}
                      {r.produitCode || r.provinceCode || r.categorieInstallation ? ', déclinaison' : ''}
                    </small>
                  </button>
                </td>
                {(['informative', 'preventive', 'elevee', 'critique'] as const).map((n) => (
                  <td key={n} className="nombre">
                    {r.seuils[n] === null || r.seuils[n] === undefined ? <span className="cellule-indisponible">sans objet</span> : formaterNombre(r.seuils[n]!, 1)}
                    <small>{r.delaisHeures[n]} h</small>
                  </td>
                ))}
                <td>{!r.evaluee ? <span className="etiquette gris">Non évaluée</span> : r.actif ? <span className="etiquette">Active</span> : <span className="etiquette gris">Inactive</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EditionRegle({ regle, modifiable, fermer, rafraichir }: { regle: RegleAlerte; modifiable: boolean; fermer: () => void; rafraichir: () => void }) {
  const historique = useQuery({ queryKey: ['alertes', 'regles', regle.id, 'historique'], queryFn: async () => (await api<{ id: string; version: number; motif: string; creeLe: string; auteur: { prenom: string; nom: string } | null }[]>(`/api/v1/regles-alerte/${regle.id}/historique`)).donnees });
  const niveaux = ['informative', 'preventive', 'elevee', 'critique'] as const;
  const [seuils, setSeuils] = useState<Record<string, string>>(Object.fromEntries(niveaux.map((n) => [n, regle.seuils[n] === null || regle.seuils[n] === undefined ? '' : String(regle.seuils[n])])));
  const [delais, setDelais] = useState<Record<string, string>>(Object.fromEntries(niveaux.map((n) => [n, String(regle.delaisHeures[n] ?? '')])));
  const [actif, setActif] = useState(regle.actif);
  const [motif, setMotif] = useState('');
  const envoi = useMutation({
    mutationFn: async () =>
      api(`/api/v1/regles-alerte/${regle.id}`, {
        methode: 'PATCH',
        corps: {
          seuils: Object.fromEntries(niveaux.map((n) => [n, seuils[n] === '' ? null : Number(seuils[n]!.replace(',', '.'))])),
          delaisHeures: Object.fromEntries(niveaux.map((n) => [n, Number(delais[n]!.replace(',', '.'))])),
          actif,
          motif,
        },
      }),
    onSuccess: () => {
      rafraichir();
      setMotif('');
    },
  });
  return (
    <form
      className="panneau-saisie"
      onSubmit={(e) => {
        e.preventDefault();
        envoi.mutate();
      }}
    >
      <h2>{regle.libelle}</h2>
      {!regle.evaluee && regle.motifNonEvaluee && <p className="note">{regle.motifNonEvaluee}</p>}
      <p className="attenue">
        Destinataires : {regle.destinataires.organisation.join(', ') || 'aucun'} ; escalade : {regle.destinataires.zone.join(', ') || 'aucune'}, puis {regle.destinataires.dgh.join(', ') || 'aucune'}.
      </p>
      <div className="formulaire-ligne">
        {niveaux.map((n) => (
          <div key={n} className="champ">
            <label htmlFor={`rg-seuil-${n}`}>Seuil {LIBELLES_NIVEAUX[n].toLowerCase()}</label>
            <input id={`rg-seuil-${n}`} inputMode="decimal" value={seuils[n]} disabled={!modifiable} onChange={(e) => setSeuils({ ...seuils, [n]: e.target.value })} />
            <label htmlFor={`rg-delai-${n}`}>Délai de traitement (h)</label>
            <input id={`rg-delai-${n}`} inputMode="decimal" value={delais[n]} disabled={!modifiable} onChange={(e) => setDelais({ ...delais, [n]: e.target.value })} />
          </div>
        ))}
      </div>
      {modifiable && (
        <>
          <label className="case">
            <input type="checkbox" checked={actif} onChange={(e) => setActif(e.target.checked)} />
            <span>Règle active</span>
          </label>
          <div className="champ">
            <label htmlFor="rg-motif">Motif de la modification (obligatoire)</label>
            <input id="rg-motif" value={motif} onChange={(e) => setMotif(e.target.value)} required minLength={3} />
          </div>
        </>
      )}
      {envoi.error && <Message type="erreur">{messageErreur(envoi.error)}</Message>}
      {envoi.isSuccess && <Message type="succes">Règle modifiée ; la nouvelle version s’applique à la prochaine passe du moteur.</Message>}
      <h3 className="espace-haut">Historique des versions</h3>
      <ol className="chronologie">
        {(historique.data ?? []).map((h) => (
          <li key={h.id}>
            Version {h.version} : {h.motif}
            <small>
              {dateHeure(h.creeLe)}, {h.auteur ? `${h.auteur.prenom} ${h.auteur.nom}` : 'système'}
            </small>
          </li>
        ))}
      </ol>
      <div className="actions espace-haut">
        {modifiable && (
          <button type="submit" className="bouton primaire" disabled={envoi.isPending}>
            Enregistrer la nouvelle version
          </button>
        )}
        <button type="button" className="bouton" onClick={fermer}>
          Fermer
        </button>
      </div>
    </form>
  );
}
